// Phase 0085 — Task execution + scoring + awards API client.
import apiClient from '@/shared/api/client'

// ── Types ───────────────────────────────────────────────────────────────

export type TaskStatus =
  | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'VERIFIED' | 'REJECTED'
  | 'OVERDUE' | 'BLOCKED' | 'SKIPPED' | 'CANCELLED'

export interface TaskInstance {
  id: string
  template_id: string | null
  role_responsibility_id: string | null
  role_responsibility_line_id: string | null
  title: string
  description: string | null
  priority: string | null
  scheduled_for_date: string
  scheduled_shift: string | null
  due_at: string | null
  assigned_to_user_id: string | null
  assigned_to_employee_id: string | null
  assigned_to_role_id: string | null
  verifier_user_id: string | null
  verifier_role_id: string | null
  status: TaskStatus
  started_at: string | null
  completed_at: string | null
  completed_by: string | null
  verified_at: string | null
  verified_by: string | null
  escalation_level: number
  escalated_at: string | null
  evidence_required: boolean
  evidence_url: string | null
  score_weight: number
  blocked_reason_id: string | null
  blocked_reason_text: string | null
  auto_completion_source: string | null
  remarks: string | null
  // Phase 0090 — Study ↔ To-Do bridge
  study_id: string | null
  study_template_id: string | null
}

export interface TaskTemplate {
  id: string
  template_code: string
  role_responsibility_line_id: string
  role_responsibility_id: string
  title: string
  description: string | null
  recurrence_pattern: string
  default_due_offset_minutes: number
  default_priority: string | null
  default_assignee_mode: string
  default_assignee_user_id: string | null
  evidence_required: boolean
  score_weight: number
  auto_completion_hook: string | null
  active: boolean
}

export interface EscalationRow {
  id: string
  task_instance_id: string
  escalation_level: number
  escalated_to_user_id: string | null
  escalated_to_role_id: string | null
  triggered_at: string
  ack_at: string | null
  resolution_status: string
  remarks: string | null
}

export interface DailyScore {
  id: string
  employee_id: string
  user_id: string | null
  score_date: string
  tasks_assigned: number
  tasks_completed: number
  tasks_verified: number
  tasks_overdue: number
  tasks_blocked_fair: number
  tasks_rejected: number
  completion_pct: number
  timeliness_pct: number
  verification_quality_pct: number
  attendance_pct: number
  improvement_pct: number
  total_score: number
}

export interface MonthlyScore extends Omit<DailyScore, 'score_date'> {
  year: number
  month: number
  rank_in_department: number | null
  rank_overall: number | null
}

export interface DepartmentMonthly {
  id: string
  department_id: string
  year: number
  month: number
  employee_count: number
  avg_score: number
  tasks_assigned: number
  tasks_completed: number
  tasks_overdue: number
  completion_pct: number
  rank_overall: number | null
}

export interface PerformanceAward {
  id: string
  award_year: number
  award_month: number
  award_type: string
  department_id: string | null
  employee_id: string | null
  score_value: number
  rank_position: number
  citation: string | null
  is_published: boolean
}

export interface ExceptionReason {
  id: string
  reason_code: string
  reason_name: string
  category: string
  penalize_employee_flag: boolean
  requires_evidence: boolean
}

// ── API ─────────────────────────────────────────────────────────────────

const T = '/hr/tasks'
const S = '/hr/scoring'
const A = '/hr/awards'

export const taskApi = {
  // Templates
  listTemplates: async (params: { active_only?: boolean; role_id?: string } = {}) =>
    (await apiClient.get<TaskTemplate[]>(`${T}/templates`, { params })).data,
  createTemplate: async (body: Partial<TaskTemplate> & { role_responsibility_line_id: string }) =>
    (await apiClient.post<TaskTemplate>(`${T}/templates`, body)).data,
  updateTemplate: async (id: string, body: Partial<TaskTemplate>) =>
    (await apiClient.patch<TaskTemplate>(`${T}/templates/${id}`, body)).data,

  // Generation
  generate: async (target_date: string, shift?: string) =>
    (await apiClient.post(`${T}/generate`, { target_date, shift })).data,

  // Queues
  myTasks: async (on_date?: string, status?: string) =>
    (await apiClient.get<TaskInstance[]>(`${T}/my`, { params: { on_date, status } })).data,
  teamTasks: async (on_date?: string) =>
    (await apiClient.get<TaskInstance[]>(`${T}/team`, { params: { on_date } })).data,
  verifyQueue: async (on_date?: string) =>
    (await apiClient.get<TaskInstance[]>(`${T}/verify-queue`, { params: { on_date } })).data,

  // Calendar
  calendar: async (from_date: string, to_date: string, user_id?: string) =>
    (await apiClient.get<TaskInstance[]>(`${T}/calendar`, {
      params: { from: from_date, to: to_date, user_id },
    })).data,

  // Lifecycle
  getTask: async (id: string) =>
    (await apiClient.get<TaskInstance>(`${T}/${id}`)).data,
  start: async (id: string) =>
    (await apiClient.post<TaskInstance>(`${T}/${id}/start`)).data,
  complete: async (id: string, body: { evidence_url?: string; remarks?: string } = {}) =>
    (await apiClient.post<TaskInstance>(`${T}/${id}/complete`, body)).data,
  // Phase HR-002 — upload evidence file (photo / PDF) and get back the
  // /static URL the caller plugs into `complete({ evidence_url })`.
  uploadEvidence: async (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post<{
      task_id: string; evidence_url: string; evidence_path: string;
      size_bytes: number; uploaded_by: string
    }>(`${T}/${id}/upload-evidence`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data
  },
  verify: async (id: string, body: { result: string; quality_score?: number; remarks?: string }) =>
    (await apiClient.post<TaskInstance>(`${T}/${id}/verify`, body)).data,
  block: async (id: string, body: { reason_code: string; reason_text?: string }) =>
    (await apiClient.post<TaskInstance>(`${T}/${id}/block`, body)).data,
  exceptionReasons: async () =>
    (await apiClient.get<ExceptionReason[]>(`${T}/exception-reasons`)).data,

  // Escalation
  runEscalation: async () =>
    (await apiClient.post(`${T}/escalation/run`)).data,
  escalationInbox: async (resolution_status = 'OPEN') =>
    (await apiClient.get<EscalationRow[]>(`${T}/escalation/inbox`, { params: { resolution_status } })).data,
  ackEscalation: async (id: string, remarks?: string) =>
    (await apiClient.post(`${T}/escalation/${id}/ack`, { remarks })).data,
  resolveEscalation: async (id: string, remarks?: string) =>
    (await apiClient.post(`${T}/escalation/${id}/resolve`, { remarks })).data,
}

export const scoringApi = {
  rules: async () => (await apiClient.get(`${S}/rules`)).data,
  createRule: async (body: Record<string, unknown>) =>
    (await apiClient.post(`${S}/rules`, body)).data,
  computeDaily: async (score_date: string, employee_id?: string) =>
    (await apiClient.post(`${S}/compute-daily`, null, { params: { score_date, employee_id } })).data,
  computeMonthly: async (year: number, month: number) =>
    (await apiClient.post(`${S}/compute-monthly`, null, { params: { year, month } })).data,
  employeeDaily: async (employee_id: string, from_date: string, to_date: string) =>
    (await apiClient.get<DailyScore[]>(`${S}/employee/${employee_id}/daily`, {
      params: { from: from_date, to: to_date },
    })).data,
  employeeMonthly: async (employee_id: string, year?: number) =>
    (await apiClient.get<MonthlyScore[]>(`${S}/employee/${employee_id}/monthly`, { params: { year } })).data,
  departmentMonthly: async (department_id: string, year?: number) =>
    (await apiClient.get<DepartmentMonthly[]>(`${S}/department/${department_id}/monthly`, { params: { year } })).data,
  leaderboard: async (year: number, month: number, limit = 20, department_id?: string) =>
    (await apiClient.get<MonthlyScore[]>(`${S}/leaderboard`, {
      params: { year, month, limit, department_id },
    })).data,
}

export const awardApi = {
  generate: async (year: number, month: number) =>
    (await apiClient.post<{ awards: PerformanceAward[]; total_generated: number }>(`${A}/generate`, null, { params: { year, month } })).data,
  list: async (year?: number, month?: number, published_only = false) =>
    (await apiClient.get<PerformanceAward[]>(A, { params: { year, month, published_only } })).data,
  publish: async (id: string) =>
    (await apiClient.post<PerformanceAward>(`${A}/${id}/publish`)).data,
}
